import { useEffect, useState } from "react";
import { IServicePlan } from "../interface";
import { Button } from "components/buttonV2";
import { Link } from "react-router-dom";
import { Status } from "components/status";
import { useTranslation } from "react-i18next";
import { useAuthContext } from "contexts/auth";
import convertCVD from "utils/convertCVD";
import axios from "axios";
import { RiFileTextLine } from "react-icons/ri";
import { FaRedoAlt } from "react-icons/fa";
import TooltippedElement from "components/TooltippedElement";
import { useModalContext } from "components/modal";
import ConfirmSendModal from "views/service-plans/components/ConfirmSendModal"; // comes from customer service plan
import { RiAlarmWarningLine } from "react-icons/ri";
import { getContrastTextColorHEX } from "utils/getContrastTextColor";
import { format } from "date-fns";
import { TbNotesOff } from "react-icons/tb";
import { FcDeleteDatabase } from "react-icons/fc";

interface IProps {
  data: IServicePlan;
  onClose: () => void;
  getPdfContract: (info: { servicePlanId: string; customerId: string }) => Promise<string>;
}

const ServicePlanModal = ({ data, onClose, getPdfContract }: IProps) => {
  const { t } = useTranslation();
  const tBase = "views.service-plan-dashboard.components.ServicePlanModal";
  const tr = (key: string) => t(`${tBase}.${key}`);

  const { cvd_type } = useAuthContext();
  const { setModal } = useModalContext();

  const [archiveData, setArchiveData] = useState({
    archived: data.archived,
    archived_at: "",
    archived_by: "",
  });
  const [appointmentTypes, setAppointmentTypes] = useState<{ id: string, label: string, color: string, quantity: number }[]>([]);
  const [customerTags, setCustomerTags] = useState<{ id: string, label: string, color: string }[]>([]);
  const [propertyTags, setPropertyTags] = useState<{ id: string, label: string, color: string }[]>([]);

  const {
    sptc_id,
    service_plan_id,
    customer_id,
    email,
    sent_emails,
    customer_name,
    company_name,
    plan_name,
    terms,
    description,
    property,
    approval_date,
    status,
    duration,
    is_contract,
    auto_renew,
    expiring_soon,
    start_date,
    last_sent,
    decline_note,
    failed_note,
  } = data;

  const statusColor = convertCVD(status.color, cvd_type);

  const getAppointmentTypes = async () => {
    const res = await axios.get(`${process.env.REACT_APP_SERVER_URL}/api/v1/service-plans/get-appointment-types`, {
      params: { servicePlanId: service_plan_id },
    });
    if (res.status === 200) {
      setAppointmentTypes(res.data);
    }
    return res.data;
  };

  const getTags = async () => {
    const res = await axios.get(`${process.env.REACT_APP_SERVER_URL}/api/v1/service-plans/get-tags`, {
      params: { 
        customerId: customer_id,
        propertyId: property.id,
      },
    });
    if (res.status === 200) {
      setCustomerTags(res.data.customerTags);
      setPropertyTags(res.data.propertyTags);
    }
    return res.data;
  }

  const getArchiver = async () => {
    const res = await axios.get(`${process.env.REACT_APP_SERVER_URL}/api/v1/service-plans/get-archiver`, {
      params: { servicePlanId: sptc_id },
    });
    if (res.status === 200) {
      setArchiveData(res.data);
    }
    return res.data;
  };

  useEffect(() => {
    getArchiver();
    getAppointmentTypes();
    getTags();
  }, []);

  const archiver = async () => {
    const res = await axios.post(`${process.env.REACT_APP_SERVER_URL}/api/v1/service-plans/archive`, {
      servicePlanId: sptc_id,
      bool: !archiveData.archived,
    })
    if (res.status === 200) {
      getArchiver();
    }
    return res.data;
  };

  const detailDiv = (title: string, content: any) => {
    return (
      <div className="flex flex-col items-end">
        <p className="text-gray-600 font-semibold underline underline-offset-[3px] decoration-gray-400">{title}</p>
        <p className="text-gray-500 text-sm">
          {content}
        </p>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="w-[800px]">
      <div className="bg-white rounded-lg shadow-lg w-full max-h-full overflow-hidden">
        {/* Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b-2 border-primary">
          <div className="-mb-2.5">
            <div className="text-xl font-bold text-gray-800">{plan_name}</div>
            <div className="text-sm text-gray-500">{tr("ID")}: {service_plan_id}</div>
          </div>
          <div className="flex items-center gap-3">
            {archiveData.archived && (
              <TooltippedElement
                message={`${tr("Archived on")} ${new Date(
                  archiveData.archived_at
                ).toLocaleDateString()} ${tr("by")} ${archiveData.archived_by}`}
                placement="top"
                element={<FcDeleteDatabase size={24} />}
              />
            )}
            {expiring_soon && status.id !== "46" && (
              <TooltippedElement
                message={tr("Expiring Soon")}
                placement="top"
                element={<RiAlarmWarningLine size={20} className="text-red-500" />}
              />
            )}
            {auto_renew && (
              <TooltippedElement
                message={tr("Auto Renew")}
                placement="top"
                element={<FaRedoAlt size={20} className="text-blue-500" />}
              />
            )}
            {is_contract && (
              <TooltippedElement
                message={tr("View Contract")}
                placement="top"
                element={
                  <button
                    onClick={() => {
                      getPdfContract({
                        servicePlanId: service_plan_id,
                        customerId: customer_id,
                      }).then((url) => window.open(url));
                    }}
                    className="p-2 rounded-full bg-slate-100 hover:bg-slate-300 transition duration-200"
                  >
                    <RiFileTextLine size={20} className="text-primary" />
                  </button>
                }
              />
            )}
            {decline_note && (
              <TooltippedElement
                message={tr("View Decline Note")}
                placement="top"
                element={
                  <button
                    onClick={() => 
                      setModal({
                        label: tr("Decline Note"),
                        component: (
                          <div className="p-4 text-gray-700 max-w-[500px]">
                            <div className="whitespace-pre-wrap mb-4">
                              {decline_note}
                            </div>
                            <Button
                              type="secondary"
                              onClick={() => setModal()}
                            >
                              {tr("Close")}
                            </Button>
                          </div>
                        )
                      })
                    }
                    className="p-2 rounded-full bg-slate-100 hover:bg-slate-300 transition duration-200"
                  >
                    <TbNotesOff size={20} className="text-yellow-600" />
                  </button>
                }
              />
            )}
            {failed_note && (
              <TooltippedElement
                message={tr("View Failed Note")}
                placement="top"
                element={
                  <button
                    onClick={() => 
                      setModal({
                        label: tr("Failed Note"),
                        component: (
                          <div className="p-4 text-gray-700 max-w-[500px]">
                            <div className="whitespace-pre-wrap mb-4">
                              {failed_note}
                            </div>
                            <Button
                              type="secondary"
                              onClick={() => setModal()}
                            >
                              {tr("Close")}
                            </Button>
                          </div>
                        )
                      })
                    }
                    className="p-2 rounded-full bg-slate-100 hover:bg-slate-300 transition duration-200"
                  >
                    <TbNotesOff size={20} className="text-red-600" />
                  </button>
                }
              />
            )}
            <Status color={statusColor}>{status.label}</Status>
          </div>
        </div>

        {/* Appointment Types */}
        <div className="px-6 py-2 border-b border-gray-200">
          <h4 className="font-semibold text-gray-800 mb-2">
            {tr("Appointment Types")}
          </h4>
          <div className="flex overflow-x-auto gap-2 pb-2">
            {appointmentTypes.length > 0 ? (
              appointmentTypes.map((type) => (
                <div
                  key={type.id}
                  className="flex justify-between items-center p-2 border rounded bg-gray-50"
                >
                  <span className="whitespace-nowrap mr-2">{type.label}</span>
                  <span className="text-sm bg-gray-200 rounded-full px-2 py-1">
                    {type.quantity}
                  </span>
                </div>
              ))
            ) : (
              <span className="text-gray-500">
                {tr("None")}
              </span>
            )}
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-4 overflow-y-auto">
          <div className="grid grid-cols-12 gap-4 mb-6">

            {/* Customer + Property */}
            <div className="col-span-8 flex flex-col space-y-3">
              <div>
                <div className="flex items-center space-x-2">
                  <p className="font-medium text-gray-800">{tr("Customer")}</p>
                  {customerTags.length > 0 && (
                    <div className="flex space-x-2 overflow-y-auto">
                      {customerTags.map((tag) => (
                        <span
                          key={tag.id}
                          className="text-xs px-2 py-1 rounded-full text-white"
                          style={{
                            backgroundColor: tag.color,
                            color: getContrastTextColorHEX(tag.color),
                            border: tag.color === "#ffffff" ? "1px solid #000000" : "none",
                          }}
                        >
                          {tag.label}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <Link
                  to={`/customers/${customer_id}`}
                  className="text-secondary"
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent the modal from closing when clicking the link
                  }}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {customer_name} {company_name && `(${company_name})`}
                </Link>
              </div>
              <div>
                <div className="flex items-center space-x-2">
                  <p className="font-medium text-gray-800">{tr("Property")}</p>
                  {propertyTags.length > 0 && (
                    <div className="flex space-x-2 overflow-y-auto">
                      {propertyTags.map((tag) => (
                        <span
                          key={tag.id}
                          className="text-xs px-2 py-1 rounded-full text-white"
                          style={{
                            backgroundColor: tag.color,
                            color: getContrastTextColorHEX(tag.color),
                            border: tag.color === "#ffffff" ? "1px solid #000000" : "none",
                          }}
                        >
                          {tag.label}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <Link
                  to={`/property/${property.id}`}
                  className="text-secondary"
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent the modal from closing when clicking the link
                  }}
                  // new tab
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {property.address} {property.city && `, ${property.city}`}{" "}
                  {property.state && `, ${property.state}`}{" "}
                  {property.zipcode && `, ${property.zipcode}`}
                </Link>
              </div>
            </div>
            
            {/* Details */}
            <div className="col-span-2 flex flex-col space-y-3">
              {detailDiv(tr("Start Date"), start_date ? new Date(start_date).toLocaleDateString() : "- -")}
              {detailDiv(tr("Duration"), duration ? `${duration} ${tr("months")}` : "- -")}
            </div>
            <div className="col-span-2 flex flex-col space-y-3">
              {detailDiv(tr("Approval Date"), approval_date ? new Date(approval_date).toLocaleDateString() : "- -")}
              {detailDiv(("Expiration Date"), 
                start_date && duration ?
                  new Date(new Date(start_date).setMonth(new Date(start_date).getMonth() + duration)).toLocaleDateString()
                  : "- -"
              )}
            </div>
          </div>

          {/* Terms & Description */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <section>
              <h4 className="font-semibold text-gray-800 mb-2">{tr("Terms")}</h4>
              <div className="max-h-[200px] p-4 border border-gray-200 rounded overflow-y-auto">
                <p className="text-gray-700 text-sm whitespace-pre-wrap">
                  {terms}
                </p>
              </div>
            </section>
            <section>
              <h4 className="font-semibold text-gray-800 mb-2">{tr("Description")}</h4>
              <div className="max-h-[200px] p-4 border border-gray-200 rounded overflow-y-auto">
                <p className="text-gray-700 text-sm whitespace-pre-wrap">
                  {description}
                </p>
              </div>
            </section>
          </div>
        </div>

        {/* Footer */}
        <div className="w-full py-4 border-t border-gray-200 flex justify-between">
          <Button type="secondary" onClick={onClose}>
            {tr("Close")}
          </Button>
          <div className="flex items-center space-x-3">
            {is_contract && (
              <>
                {last_sent && (
                  <div className=" text-xs text-gray-500 ">
                    {tr("Last sent")} {format(new Date(last_sent), 'MM/dd/yyyy')}
                  </div>
                )}
                <Button
                  type="primary"
                  onClick={() =>
                    setModal({
                      label: tr("Resend Contract"),
                      component: (
                        <ConfirmSendModal
                          servicePlanId={service_plan_id}
                          propertyId={property.id}
                          customerEmail={email}
                          customerEmailsSaved={sent_emails}
                          customerId={customer_id}
                          type="resend"
                        />
                      ),
                    })
                  }
                >
                  {tr("Resend")}
                </Button>
              </>
            )}
            <Button
              type={archiveData.archived ? "success" : "alert"}
              onClick={archiver}
            >
              {archiveData.archived ? tr("Unarchive") : tr("Archive")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServicePlanModal;